import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { useRouter } from "expo-router";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";
const TEXT_MUTED = "#6B7280";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function P({ children, style }: { children: React.ReactNode; style?: object }) {
  return <Text style={[styles.p, style]}>{children}</Text>;
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.li}>
      <Text style={styles.liBullet}>•</Text>
      <Text style={styles.liText}>{children}</Text>
    </View>
  );
}

export default function TermsScreen() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
        >
          <Text style={styles.backText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Terms of Service</Text>
        <View style={{ width: 60 }} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Terms of Service</Text>
          <Text style={styles.heroDate}>Last updated: April 18, 2026</Text>
        </View>

        <View style={styles.body}>
          <P>
            Please read these Terms of Service ("Terms") carefully before using
            the DashHound mobile and web application (the "App") operated by
            Dash-Hound LLC, a New Jersey limited liability company ("us," "we," or "our").
          </P>
          <P>
            By accessing or using the App, you agree to be bound by these Terms.
            If you do not agree to any part of these Terms, you may not access
            the App.
          </P>

          <Section title="1. Acceptance of Terms">
            <P>
              By creating an account or using DashHound, you confirm that you are
              at least 13 years of age and that you have read, understood, and
              agree to be bound by these Terms and our Privacy Policy.
            </P>
          </Section>

          <Section title="2. Description of Service">
            <P>
              DashHound is a free companion app for AKC Fast CAT enthusiasts that
              allows users to:
            </P>
            <Li>Log and track Fast CAT run times and calculate speed/points</Li>
            <Li>View upcoming AKC Fast CAT events sourced from public AKC data</Li>
            <Li>Browse AKC Fast CAT breed rankings sourced from public AKC data</Li>
            <Li>Track progress toward AKC title milestones</Li>
            <Li>See your dog's projected breed ranking based on logged performance</Li>
            <Li>Import run history from other apps via screenshot or CSV</Li>
            <Li>Share your dog's profile via a public link (opt-in)</Li>
            <Li>Receive email notifications about events and ranking changes (opt-in)</Li>
          </Section>

          <Section title="3. Free Service">
            <P>
              DashHound is currently provided free of charge. We reserve the right
              to introduce optional paid features or subscriptions in the future,
              but we will notify users before any paid features are introduced. Core
              tracking functionality will remain free.
            </P>
          </Section>

          <Section title="4. User Accounts">
            <P>
              You are responsible for safeguarding the password used to access the
              App and for any activities or actions taken under your password. You
              agree to notify us immediately of any unauthorized use of your account.
            </P>
            <P>
              You are solely responsible for the accuracy of information you enter
              into the App, including run times, dates, and dog information. We do
              not verify the accuracy of user-submitted data.
            </P>
          </Section>

          <Section title="5. AKC Data Disclaimer">
            <P>
              DashHound displays event listings and breed rankings sourced from
              publicly available American Kennel Club (AKC) resources. We are not
              affiliated with, endorsed by, or sponsored by the AKC. AKC, Fast CAT,
              and related marks are trademarks of the American Kennel Club.
            </P>
            <P>
              We do not guarantee the accuracy, completeness, or timeliness of AKC
              event and rankings data displayed in the App. This data is scraped
              from public sources and may be delayed or contain errors. For official
              and authoritative information, please visit akc.org directly.
            </P>
            <P>
              You agree not to hold DashHound liable for any decisions made based
              on the AKC data displayed in the App.
            </P>
          </Section>

          <Section title="6. Accuracy of User Data">
            <P>
              You are responsible for the accuracy and legality of all content you
              submit to the App, including run times, dog information, and any other
              data. You agree not to:
            </P>
            <Li>Submit false, misleading, or fabricated run data</Li>
            <Li>Impersonate another person or misrepresent your dog's information</Li>
            <Li>Use the App for any unlawful purpose</Li>
            <Li>Attempt to gain unauthorized access to any part of the App</Li>
            <Li>Interfere with or disrupt the integrity of the App</Li>
          </Section>

          <Section title="7. Public Dog Profiles">
            <P>
              You may choose to make your dog's profile publicly accessible via a
              shareable link. When a profile is public:
            </P>
            <Li>Anyone with the link can view the dog's name, breed, stats, and run history</Li>
            <Li>The profile may generate a preview image for social media sharing</Li>
            <Li>You can make the profile private again at any time</Li>
            <P>
              You are responsible for any information you choose to make public.
              Do not make a profile public if you do not want the information visible
              to others.
            </P>
          </Section>

          <Section title="8. Screenshot Import">
            <P>
              The App offers the ability to import run data by uploading screenshots
              from other apps. When you use this feature, your screenshot is sent to
              a third-party AI service (Anthropic Claude) for text extraction. By
              using this feature, you consent to this processing. Screenshots are
              not stored after processing is complete.
            </P>
          </Section>

          <Section title="9. Intellectual Property">
            <P>
              The App and its original content, features, and functionality are
              owned by Dash-Hound and are protected by intellectual property laws.
              You may not copy, modify, distribute, sell, or lease any part of our
              App without our express written permission.
            </P>
            <P>
              You retain ownership of any data you enter into the App (your run
              logs, dog profiles, etc.). By using the App, you grant us a limited,
              non-exclusive license to store and process this data solely to provide
              the App's services to you.
            </P>
          </Section>

          <Section title="10. Account Termination">
            <P>
              We reserve the right to suspend or terminate your account at any time,
              with or without cause, including but not limited to:
            </P>
            <Li>Violation of these Terms</Li>
            <Li>Fraudulent or abusive use of the App</Li>
            <Li>Extended inactivity</Li>
            <Li>Requests from law enforcement</Li>
            <P>
              You may delete your account at any time from the App's Settings page.
              Account deletion is immediate and permanent — all your dogs, runs,
              photos, and profile data will be permanently removed. You may also
              request deletion by emailing dashhoundapp@gmail.com.
            </P>
          </Section>

          <Section title="11. Disclaimer of Warranties">
            <P>
              THE APP IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT
              ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
              NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, OR NON-INFRINGEMENT.
            </P>
            <P>
              WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR
              FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. WE DO NOT WARRANT THE
              ACCURACY OR COMPLETENESS OF ANY INFORMATION PROVIDED THROUGH THE APP.
            </P>
          </Section>

          <Section title="12. Limitation of Liability">
            <P>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
              DASH-HOUND BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS
              OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT
              OF OR IN CONNECTION WITH YOUR USE OF THE APP.
            </P>
            <P>
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING
              FROM YOUR USE OF THE APP EXCEED $100.
            </P>
          </Section>

          <Section title="13. Indemnification">
            <P>
              You agree to defend, indemnify, and hold harmless Dash-Hound and its
              officers, directors, employees, and agents from any claims, liabilities,
              damages, costs, and expenses arising from your use of the App, your
              violation of these Terms, or your violation of any third party's rights.
            </P>
          </Section>

          <Section title="14. Privacy">
            <P>
              Your use of the App is also governed by our Privacy Policy, which is
              incorporated into these Terms by reference. Please review our Privacy
              Policy to understand our practices.
            </P>
          </Section>

          <Section title="15. Changes to Terms">
            <P>
              We reserve the right to modify these Terms at any time. We will
              provide notice of material changes by posting the updated Terms in the
              App and updating the "Last updated" date above. Your continued use of
              the App after any changes constitutes your acceptance of the new Terms.
            </P>
          </Section>

          <Section title="16. Governing Law">
            <P>
              These Terms shall be governed by and construed in accordance with the
              laws of the United States, without regard to its conflict of law
              provisions. Any disputes arising under these Terms shall be resolved
              through binding arbitration rather than in court, except that either
              party may seek injunctive relief in court for violation of intellectual
              property rights.
            </P>
          </Section>

          <Section title="17. Contact Us">
            <P>
              If you have questions about these Terms of Service, please contact us:
            </P>
            <P style={{ fontWeight: "600", color: NAVY }}>
              dashhoundapp@gmail.com
            </P>
          </Section>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © 2026 Dash-Hound LLC. All rights reserved.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  header: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    paddingTop: Platform.OS === "ios" ? 50 : 14,
  },
  backButton: {
    width: 60,
  },
  backText: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 15,
    fontWeight: "500",
  },
  headerTitle: {
    color: WHITE,
    fontSize: 16,
    fontWeight: "700",
  },
  hero: {
    backgroundColor: NAVY,
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 16,
    alignItems: "center",
  },
  heroTitle: {
    color: WHITE,
    fontSize: 32,
    fontWeight: "900",
    letterSpacing: -0.5,
  },
  heroDate: {
    color: "rgba(255,255,255,0.5)",
    fontSize: 13,
    marginTop: 8,
  },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 40,
    gap: 4,
  },
  section: {
    marginTop: 32,
    gap: 8,
  },
  sectionTitle: {
    color: NAVY,
    fontSize: 18,
    fontWeight: "800",
    marginBottom: 8,
    letterSpacing: -0.2,
  },
  p: {
    color: TEXT,
    fontSize: 15,
    lineHeight: 26,
  },
  li: {
    flexDirection: "row",
    gap: 10,
    paddingLeft: 8,
  },
  liBullet: {
    color: ORANGE,
    fontSize: 16,
    lineHeight: 26,
    flexShrink: 0,
  },
  liText: {
    flex: 1,
    color: TEXT,
    fontSize: 15,
    lineHeight: 26,
  },
  footer: {
    backgroundColor: NAVY_DARK,
    paddingVertical: 32,
    alignItems: "center",
  },
  footerText: {
    color: "rgba(255,255,255,0.35)",
    fontSize: 13,
  },
});
